// =====================================================================
// Convênios / Controle de Glosa (S120) — registra guia cobrada x resposta do
// plano (pago/glosado), controle de sessões e senha, e gera o DOSSIÊ pra
// PROVAR/recorrer a negativa. Gated convenio_module_enabled (CLEE). Sem IA;
// números determinísticos. Estilos escopados (makeStyles). Reusa iframe+print
// pro PDF (zero lib), padrão da tela de Faltosos.
// =====================================================================
import React, { useEffect, useState, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, Typography,
  CircularProgress, Box, TextField, Chip, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid,
  Divider
} from "@material-ui/core";
import {
  Add, Edit, Delete, Refresh, PictureAsPdf, Publish, WarningRounded
} from "@material-ui/icons";
import moment from "moment";

import {
  listGuias, getSummary, getConvenios, createGuia, updateGuia, deleteGuia, importGuias
} from "../../services/convenioService";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CONVENIO_PLANOS, { logoDeConvenio } from "../../constants/convenioPlanos";

// ---- domínios --------------------------------------------------------
const STATUS = {
  autorizada:     { label: "Autorizada",     color: "#2563eb" },
  em_atendimento: { label: "Em atendimento", color: "#0891b2" },
  concluida:      { label: "Concluída",      color: "#0d9488" },
  faturada:       { label: "Faturada",       color: "#7c3aed" },
  pago:           { label: "Pago",           color: "#1f9d55" },
  glosado:        { label: "Glosado",        color: "#d64545" },
  recurso:        { label: "Em recurso",     color: "#c98a00" },
  recuperado:     { label: "Recuperado",     color: "#16a34a" },
  mantido:        { label: "Glosa mantida",  color: "#991b1b" },
  cancelada:      { label: "Cancelada",      color: "#8b8f9a" }
};
const STATUS_KEYS = Object.keys(STATUS);
const SISTEMAS = ["Portal", "SAVI", "Solus", "FacPlan", "Manual/Papel", "Outro"];

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fdate = (d) => (d ? moment(d).format("DD/MM/YY") : "—");

const EMPTY = {
  convenio: "", sistemaOrigem: "", registroAns: "", codigoNaOperadora: "",
  beneficiarioNome: "", carteira: "", carteiraValidade: "",
  guiaPrestador: "", guiaOperadora: "", senha: "", dataAutorizacao: "", senhaValidade: "",
  solicitanteNome: "", indicacaoClinica: "",
  tabela: "", procedimentoCodigo: "", procedimentoDesc: "",
  qtdeAutorizada: "", qtdeRealizada: "",
  valorApresentado: "", valorPago: "", valorGlosado: "", valorRecuperado: "",
  motivoCodigo: "", motivoDescricao: "",
  dataAtendimento: "", dataEnvio: "", dataRetorno: "",
  status: "autorizada", observacao: ""
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    root: { padding: theme.spacing(3), overflowY: "auto", height: "calc(100vh - 48px)", background: dark ? "#0e0f13" : "#f4f5f7" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: theme.spacing(2) },
    title: { fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px", display: "flex", alignItems: "center", gap: 8 },
    subtitle: { color: dark ? "#8b8f9a" : "#6b7280", fontSize: 13, marginTop: 2 },
    actions: { display: "flex", gap: 8, flexWrap: "wrap" },
    cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: theme.spacing(2) },
    card: { background: dark ? "#181a1f" : "#fff", borderRadius: 12, padding: "14px 16px", border: `1px solid ${dark ? "#23262d" : "#e6ecf4"}` },
    cardLabel: { fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".04em", color: dark ? "#8b8f9a" : "#6b7280", fontWeight: 600 },
    cardValue: { fontSize: 22, fontWeight: 700, marginTop: 4 },
    alertRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: theme.spacing(2) },
    alert: { display: "flex", alignItems: "center", gap: 8, background: dark ? "#2a2416" : "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", borderRadius: 10, padding: "8px 14px", fontSize: 13.5, fontWeight: 600 },
    panels: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: theme.spacing(2) },
    panel: { background: dark ? "#181a1f" : "#fff", borderRadius: 12, padding: "14px 16px", border: `1px solid ${dark ? "#23262d" : "#e6ecf4"}` },
    panelTitle: { fontSize: 13, fontWeight: 700, marginBottom: 8 },
    filters: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: theme.spacing(2) },
    paper: { padding: 0, overflow: "auto" },
    money: { fontFamily: "monospace", fontSize: 13, textAlign: "right", whiteSpace: "nowrap" },
    glos: { color: "#d64545", fontWeight: 700 },
    empty: { padding: theme.spacing(6), textAlign: "center", color: dark ? "#8b8f9a" : "#6b7280" },
    grpTitle: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: dark ? "#8b8f9a" : "#6b7280", margin: "6px 0 2px" }
  };
});

export default function Convenios() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [filters, setFilters] = useState({ convenio: "", status: "", start: "", end: "", search: "" });
  const [dialog, setDialog] = useState(null);   // form (create/edit)
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([getSummary(filters), listGuias(filters)]);
      setSummary(s);
      setRows(l || []);
    } catch (e) { toastError(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getConvenios().then(setConvenios).catch(() => {}); }, []);

  const openNew = () => setDialog({ ...EMPTY });
  const openEdit = (r) => setDialog({
    id: r.id, convenio: r.convenio || "", sistemaOrigem: r.sistema_origem || "", registroAns: r.registro_ans || "",
    codigoNaOperadora: r.codigo_na_operadora || "", beneficiarioNome: r.beneficiario_nome || "", carteira: r.carteira || "",
    carteiraValidade: r.carteira_validade ? moment(r.carteira_validade).format("YYYY-MM-DD") : "",
    guiaPrestador: r.guia_prestador || "", guiaOperadora: r.guia_operadora || "", senha: r.senha || "",
    dataAutorizacao: r.data_autorizacao ? moment(r.data_autorizacao).format("YYYY-MM-DD") : "",
    senhaValidade: r.senha_validade ? moment(r.senha_validade).format("YYYY-MM-DD") : "",
    solicitanteNome: r.solicitante_nome || "", indicacaoClinica: r.indicacao_clinica || "",
    tabela: r.tabela || "", procedimentoCodigo: r.procedimento_codigo || "", procedimentoDesc: r.procedimento_desc || "",
    qtdeAutorizada: r.qtde_autorizada ?? "", qtdeRealizada: r.qtde_realizada ?? "",
    valorApresentado: r.valor_apresentado ?? "", valorPago: r.valor_pago ?? "", valorGlosado: r.valor_glosado ?? "",
    valorRecuperado: r.valor_recuperado ?? "", motivoCodigo: r.motivo_codigo || "", motivoDescricao: r.motivo_descricao || "",
    dataAtendimento: r.data_atendimento ? moment(r.data_atendimento).format("YYYY-MM-DD") : "",
    dataEnvio: r.data_envio ? moment(r.data_envio).format("YYYY-MM-DD") : "",
    dataRetorno: r.data_retorno ? moment(r.data_retorno).format("YYYY-MM-DD") : "",
    status: r.status || "autorizada", observacao: r.observacao || ""
  });

  const setF = (k, v) => setDialog((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!dialog.convenio) { toastError({ response: { data: { error: "Informe o convênio" } } }); return; }
    try {
      if (dialog.id) await updateGuia(dialog.id, dialog);
      else await createGuia(dialog);
      setDialog(null);
      load();
      getConvenios().then(setConvenios).catch(() => {});
    } catch (e) { toastError(e); }
  };

  const del = async (r) => {
    if (!window.confirm(`Excluir a guia de ${r.beneficiario_nome || r.convenio}?`)) return;
    try { await deleteGuia(r.id); load(); } catch (e) { toastError(e); }
  };

  const doImport = async () => {
    try {
      const parsed = parseTable(importText);
      if (!parsed.length) { toastError({ response: { data: { error: "Nada reconhecido. Cole com cabeçalho." } } }); return; }
      const res = await importGuias(parsed);
      setImportOpen(false); setImportText("");
      load();
      window.alert(`${res.imported} de ${res.total} linhas importadas.`);
    } catch (e) { toastError(e); }
  };

  const t = summary?.totals || {};
  const al = summary?.alertas || {};

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>💠 Convênios — Controle de Glosa</Typography>
          <Typography className={classes.subtitle}>
            Registre a guia e a resposta do plano · controle senhas/sessões · prove e recorra das glosas
          </Typography>
        </div>
        <div className={classes.actions}>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load}>Atualizar</Button>
          <Button variant="outlined" size="small" startIcon={<Publish />} onClick={() => setImportOpen(true)}>Importar</Button>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdf />} onClick={() => downloadPdf(rows, summary, filters, user)}>Dossiê PDF</Button>
          <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={openNew}>Nova guia</Button>
        </div>
      </div>

      {/* Painel */}
      <div className={classes.cards}>
        <Stat label="Apresentado" value={brl(t.apresentado)} classes={classes} />
        <Stat label="Pago" value={brl(t.pago)} classes={classes} color="#1f9d55" />
        <Stat label="Glosado" value={brl(t.glosado)} classes={classes} color="#d64545" />
        <Stat label="Glosado em aberto" value={brl(t.glosadoEmAberto)} classes={classes} color="#c2410c" />
        <Stat label="Taxa de glosa" value={`${t.taxaGlosa || 0}%`} classes={classes} />
        <Stat label="Recuperado" value={brl(t.recuperado)} classes={classes} color="#16a34a" />
      </div>

      {(al.senhasVencendo > 0 || al.sessoesEmAberto > 0) && (
        <div className={classes.alertRow}>
          {al.senhasVencendo > 0 && (
            <div className={classes.alert}><WarningRounded fontSize="small" /> {al.senhasVencendo} senha(s) vencendo em 15 dias</div>
          )}
          {al.sessoesEmAberto > 0 && (
            <div className={classes.alert}><WarningRounded fontSize="small" /> {al.sessoesEmAberto} sessão(ões) autorizada(s) ainda não realizada(s)</div>
          )}
        </div>
      )}

      <div className={classes.panels}>
        <div className={classes.panel}>
          <div className={classes.panelTitle}>Glosado por motivo (onde perde dinheiro)</div>
          {(summary?.porMotivo || []).length === 0 ? <Typography variant="body2" color="textSecondary">Sem glosa no período.</Typography> :
            (summary.porMotivo).slice(0, 6).map((m, i) => (
              <Box key={i} display="flex" justifyContent="space-between" py={0.4} fontSize={13}>
                <span>{m.codigo !== "—" ? `${m.codigo} · ` : ""}{m.descricao} <b>({m.qtd})</b></span>
                <span className={classes.glos}>{brl(m.glosado)}</span>
              </Box>
            ))}
        </div>
        <div className={classes.panel}>
          <div className={classes.panelTitle}>Por convênio</div>
          {(summary?.porConvenio || []).length === 0 ? <Typography variant="body2" color="textSecondary">Sem dados.</Typography> :
            (summary.porConvenio).slice(0, 6).map((c, i) => (
              <Box key={i} display="flex" justifyContent="space-between" py={0.4} fontSize={13}>
                <span>{c.convenio} <b>({c.guias})</b></span>
                <span>{brl(c.pago)} pago · <span className={classes.glos}>{brl(c.glosado)}</span></span>
              </Box>
            ))}
        </div>
      </div>

      {/* Filtros */}
      <div className={classes.filters}>
        <FormControl size="small" style={{ minWidth: 160 }}>
          <InputLabel>Convênio</InputLabel>
          <Select value={filters.convenio} onChange={(e) => setFilters({ ...filters, convenio: e.target.value })}>
            <MenuItem value="">Todos</MenuItem>
            {convenios.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" style={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <MenuItem value="">Todos</MenuItem>
            {STATUS_KEYS.map((k) => <MenuItem key={k} value={k}>{STATUS[k].label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="De" type="date" InputLabelProps={{ shrink: true }} value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} />
        <TextField size="small" label="Até" type="date" InputLabelProps={{ shrink: true }} value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} />
        <TextField size="small" label="Buscar (paciente/guia/senha)" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} style={{ minWidth: 220 }} />
      </div>

      {/* Tabela */}
      <Paper className={classes.paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Convênio</TableCell>
              <TableCell>Beneficiário</TableCell>
              <TableCell>Guia / Senha</TableCell>
              <TableCell>Sessões</TableCell>
              <TableCell align="right">Apresentado</TableCell>
              <TableCell align="right">Pago</TableCell>
              <TableCell align="right">Glosado</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={11} align="center"><Box py={3}><CircularProgress size={26} /></Box></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={11}><div className={classes.empty}>Nenhuma guia. Clique em <b>Nova guia</b> ou <b>Importar</b> uma planilha.</div></TableCell></TableRow>
            )}
            {rows.map((r) => {
              const st = STATUS[r.status] || { label: r.status, color: "#8b8f9a" };
              const venc = r.senha_validade && moment(r.senha_validade).isBefore(moment().add(15, "days")) && ["autorizada", "em_atendimento"].includes(r.status);
              return (
                <TableRow key={r.id} hover>
                  <TableCell>{fdate(r.data_retorno || r.data_atendimento || r.data_autorizacao)}</TableCell>
                  <TableCell>
                    {logoDeConvenio(r.convenio) ? <img src={logoDeConvenio(r.convenio)} alt="" style={{ height: 16, verticalAlign: "middle", marginRight: 6, background: "#fff", borderRadius: 3, padding: "1px 2px" }} /> : null}
                    {r.convenio}
                  </TableCell>
                  <TableCell>{r.beneficiario_nome || "—"}</TableCell>
                  <TableCell>
                    <div style={{ fontSize: 12.5 }}>{r.guia_prestador || r.guia_operadora || "—"}</div>
                    {r.senha ? <div style={{ fontSize: 11, color: venc ? "#c2410c" : "#8b8f9a" }}>🔑 {r.senha}{r.senha_validade ? ` · val ${fdate(r.senha_validade)}` : ""}</div> : null}
                  </TableCell>
                  <TableCell>{Number(r.qtde_realizada || 0)}/{Number(r.qtde_autorizada || 0)}</TableCell>
                  <TableCell className={classes.money}>{brl(r.valor_apresentado)}</TableCell>
                  <TableCell className={classes.money}>{brl(r.valor_pago)}</TableCell>
                  <TableCell className={classes.money}>{Number(r.valor_glosado) > 0 ? <span className={classes.glos}>{brl(r.valor_glosado)}</span> : brl(0)}</TableCell>
                  <TableCell><Chip size="small" label={st.label} style={{ background: st.color, color: "#fff", fontWeight: 600 }} /></TableCell>
                  <TableCell style={{ fontSize: 12, maxWidth: 160 }}>{r.motivo_codigo ? `${r.motivo_codigo} ${r.motivo_descricao || ""}` : "—"}</TableCell>
                  <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" onClick={() => del(r)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* ---- Dialog form ---- */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="md" fullWidth>
        {dialog && (
          <>
            <DialogTitle>{dialog.id ? "Editar guia" : "Nova guia de convênio"}</DialogTitle>
            <DialogContent dividers>
              <div className={classes.grpTitle}>Convênio</div>
              <Grid container spacing={2}>
                <Grid item xs={4}><Autocomplete
                  freeSolo size="small" options={CONVENIO_PLANOS}
                  getOptionLabel={(o) => (typeof o === "string" ? o : o.nome)}
                  inputValue={dialog.convenio || ""}
                  onInputChange={(e, v) => setF("convenio", v)}
                  renderOption={(o) => (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ background: "#fff", borderRadius: 4, padding: 2, display: "inline-flex" }}><img src={o.logo} alt="" style={{ height: 18, display: "block" }} /></span>
                      {o.nome}
                    </span>
                  )}
                  renderInput={(params) => <TextField {...params} fullWidth label="Convênio *" />}
                /></Grid>
                <Grid item xs={4}><FormControl fullWidth size="small"><InputLabel>Sistema/Origem</InputLabel><Select value={dialog.sistemaOrigem} onChange={(e) => setF("sistemaOrigem", e.target.value)}>{SISTEMAS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" label="Reg. ANS" value={dialog.registroAns} onChange={(e) => setF("registroAns", e.target.value)} /></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" label="Cód. operadora" value={dialog.codigoNaOperadora} onChange={(e) => setF("codigoNaOperadora", e.target.value)} /></Grid>
              </Grid>

              <div className={classes.grpTitle}>Beneficiário & Guia</div>
              <Grid container spacing={2}>
                <Grid item xs={5}><TextField fullWidth size="small" label="Beneficiário" value={dialog.beneficiarioNome} onChange={(e) => setF("beneficiarioNome", e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" label="Carteira" value={dialog.carteira} onChange={(e) => setF("carteira", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Nº guia (prestador)" value={dialog.guiaPrestador} onChange={(e) => setF("guiaPrestador", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Senha" value={dialog.senha} onChange={(e) => setF("senha", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Validade da senha" type="date" InputLabelProps={{ shrink: true }} value={dialog.senhaValidade} onChange={(e) => setF("senhaValidade", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Data autorização" type="date" InputLabelProps={{ shrink: true }} value={dialog.dataAutorizacao} onChange={(e) => setF("dataAutorizacao", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Solicitante" value={dialog.solicitanteNome} onChange={(e) => setF("solicitanteNome", e.target.value)} /></Grid>
              </Grid>

              <div className={classes.grpTitle}>Procedimento & sessões</div>
              <Grid container spacing={2}>
                <Grid item xs={2}><TextField fullWidth size="small" label="Tabela" value={dialog.tabela} onChange={(e) => setF("tabela", e.target.value)} /></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" label="Código" value={dialog.procedimentoCodigo} onChange={(e) => setF("procedimentoCodigo", e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" label="Descrição" value={dialog.procedimentoDesc} onChange={(e) => setF("procedimentoDesc", e.target.value)} /></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" label="Qtd autorizada" type="number" value={dialog.qtdeAutorizada} onChange={(e) => setF("qtdeAutorizada", e.target.value)} /></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" label="Qtd realizada" type="number" value={dialog.qtdeRealizada} onChange={(e) => setF("qtdeRealizada", e.target.value)} /></Grid>
              </Grid>

              <div className={classes.grpTitle}>Valores & glosa</div>
              <Grid container spacing={2}>
                <Grid item xs={3}><TextField fullWidth size="small" label="Apresentado (R$)" type="number" value={dialog.valorApresentado} onChange={(e) => setF("valorApresentado", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Pago (R$)" type="number" value={dialog.valorPago} onChange={(e) => setF("valorPago", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Glosado (R$)" type="number" value={dialog.valorGlosado} onChange={(e) => setF("valorGlosado", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Recuperado (R$)" type="number" value={dialog.valorRecuperado} onChange={(e) => setF("valorRecuperado", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Motivo glosa (cód)" value={dialog.motivoCodigo} onChange={(e) => setF("motivoCodigo", e.target.value)} /></Grid>
                <Grid item xs={9}><TextField fullWidth size="small" label="Motivo glosa (descrição)" value={dialog.motivoDescricao} onChange={(e) => setF("motivoDescricao", e.target.value)} /></Grid>
              </Grid>

              <div className={classes.grpTitle}>Datas & status</div>
              <Grid container spacing={2}>
                <Grid item xs={3}><TextField fullWidth size="small" label="Atendimento" type="date" InputLabelProps={{ shrink: true }} value={dialog.dataAtendimento} onChange={(e) => setF("dataAtendimento", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Faturado em" type="date" InputLabelProps={{ shrink: true }} value={dialog.dataEnvio} onChange={(e) => setF("dataEnvio", e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Retorno em" type="date" InputLabelProps={{ shrink: true }} value={dialog.dataRetorno} onChange={(e) => setF("dataRetorno", e.target.value)} /></Grid>
                <Grid item xs={3}><FormControl fullWidth size="small"><InputLabel>Status</InputLabel><Select value={dialog.status} onChange={(e) => setF("status", e.target.value)}>{STATUS_KEYS.map((k) => <MenuItem key={k} value={k}>{STATUS[k].label}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Observação" multiline value={dialog.observacao} onChange={(e) => setF("observacao", e.target.value)} /></Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={save}>{dialog.id ? "Salvar" : "Criar"}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ---- Dialog import ---- */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importar planilha</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Cole do Excel/CSV com <b>cabeçalho na 1ª linha</b>. Colunas reconhecidas: convenio, beneficiario, carteira, guia,
            senha, senha_validade, procedimento, qtd_autorizada, qtd_realizada, valor_apresentado, valor_pago, valor_glosado,
            motivo_codigo, motivo_descricao, data_retorno, status.
          </Typography>
          <TextField fullWidth multiline minRows={8} variant="outlined" placeholder={"convenio\tbeneficiario\tvalor_apresentado\tvalor_glosado\nHapvida\tRita Silva\t150,00\t30,00"} value={importText} onChange={(e) => setImportText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={doImport}>Importar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, classes, color }) {
  return (
    <div className={classes.card}>
      <div className={classes.cardLabel}>{label}</div>
      <div className={classes.cardValue} style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

// ---- parser de planilha colada (TSV/CSV com cabeçalho) ---------------
function parseTable(text) {
  const lines = String(text || "").trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const sep = lines[0].includes("\t") ? "\t" : (lines[0].includes(";") ? ";" : ",");
  const norm = (h) => h.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const heads = lines[0].split(sep).map(norm);
  const MAP = {
    convenio: "convenio", beneficiario: "beneficiarioNome", paciente: "beneficiarioNome", carteira: "carteira",
    guia: "guiaPrestador", senha: "senha", senha_validade: "senhaValidade", validade: "senhaValidade",
    procedimento: "procedimentoDesc", codigo: "procedimentoCodigo", tabela: "tabela",
    qtd_autorizada: "qtdeAutorizada", qtd_realizada: "qtdeRealizada",
    valor_apresentado: "valorApresentado", apresentado: "valorApresentado",
    valor_pago: "valorPago", pago: "valorPago", valor_glosado: "valorGlosado", glosado: "valorGlosado",
    motivo_codigo: "motivoCodigo", motivo: "motivoDescricao", motivo_descricao: "motivoDescricao",
    data_retorno: "dataRetorno", retorno: "dataRetorno", status: "status", sistema: "sistemaOrigem", reg_ans: "registroAns"
  };
  const money = (v) => Number(String(v || "").replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".")) || 0;
  const out = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(sep);
    const o = {};
    heads.forEach((h, idx) => {
      const key = MAP[h];
      if (!key) return;
      let v = (cells[idx] || "").trim();
      if (["valorApresentado", "valorPago", "valorGlosado"].includes(key)) v = money(v);
      o[key] = v;
    });
    if (o.convenio) out.push(o);
  }
  return out;
}

// ---- Dossiê PDF (iframe + print, zero lib) — prova/recurso -----------
function downloadPdf(rows, summary, filters, user) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const glosas = rows.filter((r) => Number(r.valor_glosado) > 0);
  const t = summary?.totals || {};
  const periodo = filters.start || filters.end ? `${filters.start ? moment(filters.start).format("DD/MM/YYYY") : "…"} a ${filters.end ? moment(filters.end).format("DD/MM/YYYY") : "…"}` : "Todo o período";
  const linhas = glosas.map((r) => `<tr>
    <td>${esc(r.convenio)}</td><td>${esc(r.beneficiario_nome || "")}</td>
    <td>${esc(r.guia_prestador || r.guia_operadora || "")}</td><td>${esc(r.senha || "")}</td>
    <td>${esc(r.procedimento_desc || r.procedimento_codigo || "")}</td>
    <td>${r.data_atendimento ? moment(r.data_atendimento).format("DD/MM/YY") : ""}</td>
    <td class="r">${brl(r.valor_apresentado)}</td><td class="r">${brl(r.valor_pago)}</td>
    <td class="r g">${brl(r.valor_glosado)}</td><td>${esc((r.motivo_codigo || "") + " " + (r.motivo_descricao || ""))}</td>
  </tr>`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Dossiê de Glosa</title><style>
    *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
    body{margin:0;padding:26px;color:#16263a}
    h1{font-size:20px;margin:0 0 2px} .sub{color:#5d6e80;font-size:12px;margin-bottom:14px}
    .tot{display:flex;gap:22px;margin:10px 0 16px;font-size:13px}
    .tot b{display:block;font-size:16px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th,td{border:1px solid #cdd6e0;padding:5px 7px;text-align:left}
    th{background:#eef3f8;font-size:10.5px} td.r{text-align:right;white-space:nowrap} td.g{color:#b23b3b;font-weight:bold}
    .ft{margin-top:16px;font-size:10px;color:#8a98a6}
  </style></head><body>
    <h1>Dossiê de Glosa — Convênios</h1>
    <div class="sub">Clínica CLEE · Período: ${esc(periodo)}${filters.convenio ? " · Convênio: " + esc(filters.convenio) : ""} · Emitido em ${moment().format("DD/MM/YYYY HH:mm")}</div>
    <div class="tot"><span>Apresentado<b>${brl(t.apresentado)}</b></span><span>Pago<b style="color:#1f7a4d">${brl(t.pago)}</b></span><span>Glosado<b style="color:#b23b3b">${brl(t.glosado)}</b></span><span>Taxa de glosa<b>${t.taxaGlosa || 0}%</b></span></div>
    <table><thead><tr><th>Convênio</th><th>Beneficiário</th><th>Guia</th><th>Senha</th><th>Procedimento</th><th>Atend.</th><th>Apres.</th><th>Pago</th><th>Glosado</th><th>Motivo</th></tr></thead>
    <tbody>${linhas || '<tr><td colspan="10" style="text-align:center;padding:20px">Sem glosas no filtro atual.</td></tr>'}</tbody></table>
    <div class="ft">Documento gerado pelo Genius Med para fins de conferência e recurso de glosa. Contém dados de saúde (LGPD) — uso restrito.</div>
  </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed"; iframe.style.right = "0"; iframe.style.bottom = "0"; iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
  document.body.appendChild(iframe);
  const d = iframe.contentWindow.document;
  d.open(); d.write(html); d.close();
  iframe.onload = () => { setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 250); };
}
